import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { DiscardDialog } from "@/components/DiscardDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { adminUpdateStore } from "@/lib/api/admin";
import type { Store } from "@/lib/api/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "@/components/Spinner";
import { TextInput } from "@/components/TextInput";
import { Textarea } from "@/components/Textarea";
import { Select } from "@/components/Select";
import type { SelectOption } from "@/components/Select";

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  category: z.enum(["restaurant", "nightclub", "beauty", "other"]),
  status: z.enum(["active", "suspended", "hidden"]),
  description: z.string().max(600, "Description must be 600 characters or less").optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  province: z.string().optional(),
  country: z.string().optional(),
  postal_code: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  public_phone: z.string().optional(),
  public_email: z.string().optional(),
  website: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

// ─── Options ──────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS: SelectOption[] = [
  { value: "restaurant", label: "Restaurant" },
  { value: "nightclub", label: "Nightclub" },
  { value: "beauty", label: "Beauty & Personal Care" },
  { value: "other", label: "Other" },
];

const STATUS_OPTIONS: SelectOption[] = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "hidden", label: "Hidden" },
];

// ─── Component ────────────────────────────────────────────────────────────────

type StoreEditDialogProps = {
  store: Store;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function StoreEditDialog({ store, open, onOpenChange }: StoreEditDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [discardOpen, setDiscardOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: store.name,
      category: store.category as "restaurant" | "nightclub" | "beauty" | "other",
      status: store.status as "active" | "suspended" | "hidden",
      description: store.description ?? "",
      address_line1: store.address_line1 ?? "",
      address_line2: store.address_line2 ?? "",
      city: store.city ?? "",
      province: store.province ?? "",
      country: store.country ?? "",
      postal_code: store.postal_code ?? "",
      phone: store.phone ?? "",
      email: store.email ?? "",
      public_phone: store.public_phone ?? "",
      public_email: store.public_email ?? "",
      website: store.website ?? "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: store.name,
        category: store.category as "restaurant" | "nightclub" | "beauty" | "other",
        status: store.status as "active" | "suspended" | "hidden",
        description: store.description ?? "",
        address_line1: store.address_line1 ?? "",
        address_line2: store.address_line2 ?? "",
        city: store.city ?? "",
        province: store.province ?? "",
        country: store.country ?? "",
        postal_code: store.postal_code ?? "",
        phone: store.phone ?? "",
        email: store.email ?? "",
        public_phone: store.public_phone ?? "",
        public_email: store.public_email ?? "",
        website: store.website ?? "",
      });
    }
  }, [open, store, reset]);

  function tryClose() {
    if (isDirty) {
      setDiscardOpen(true);
    } else {
      onOpenChange(false);
    }
  }

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => adminUpdateStore(user!, store.id, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["store", store.id] });
      void qc.invalidateQueries({ queryKey: ["stores"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      toast.success("Store updated.");
      onOpenChange(false);
    },
    onError: (err) => setError("root", { message: (err as Error).message }),
  });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Edit Store</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <Tabs defaultValue="info">
              <TabsList>
                <TabsTrigger value="info">Info</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
              </TabsList>

              {/* ── Info ── */}
              <TabsContent value="info" className="mt-4 space-y-4">
                <TextInput
                  id="se-name"
                  label="Name *"
                  error={errors.name?.message}
                  placeholder="Store name"
                  maxLength={100}
                  {...register("name")}
                />

                <div className="grid grid-cols-2 gap-3">
                  <Controller
                    control={control}
                    name="category"
                    render={({ field }) => (
                      <Select
                        id="se-category"
                        label="Category *"
                        value={field.value}
                        onValueChange={field.onChange}
                        options={CATEGORY_OPTIONS}
                        error={errors.category?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Select
                        id="se-status"
                        label="Status *"
                        value={field.value}
                        onValueChange={field.onChange}
                        options={STATUS_OPTIONS}
                        error={errors.status?.message}
                      />
                    )}
                  />
                </div>

                <Textarea
                  id="se-description"
                  label="Description"
                  error={errors.description?.message}
                  rows={6}
                  placeholder="Optional description"
                  maxLength={600}
                  {...register("description")}
                />
              </TabsContent>

              {/* ── Address ── */}
              <TabsContent value="address" className="mt-4 space-y-4">
                <TextInput
                  id="se-addr1"
                  label="Address line 1"
                  placeholder="123 Main St"
                  maxLength={200}
                  {...register("address_line1")}
                />
                <TextInput
                  id="se-addr2"
                  label="Address line 2"
                  placeholder="Suite 100"
                  maxLength={200}
                  {...register("address_line2")}
                />
                <div className="grid grid-cols-2 gap-3">
                  <TextInput
                    id="se-city"
                    label="City"
                    placeholder="City"
                    maxLength={100}
                    {...register("city")}
                  />
                  <TextInput
                    id="se-province"
                    label="Province / state"
                    placeholder="ON"
                    maxLength={100}
                    {...register("province")}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <TextInput
                    id="se-country"
                    label="Country"
                    placeholder="Canada"
                    maxLength={100}
                    {...register("country")}
                  />
                  <TextInput
                    id="se-postal"
                    label="Postal code"
                    placeholder="A1B 2C3"
                    maxLength={20}
                    {...register("postal_code")}
                  />
                </div>
              </TabsContent>

              {/* ── Contact ── */}
              <TabsContent value="contact" className="mt-4 space-y-4">
                <p className="text-xs text-muted-foreground">
                  Internal contact is visible to admins only. Public contact is shown to customers.
                </p>
                <TextInput
                  id="se-phone"
                  label="Phone (internal)"
                  placeholder="+1 416 555 0100"
                  maxLength={50}
                  {...register("phone")}
                />
                <TextInput
                  id="se-email"
                  label="Email (internal)"
                  type="email"
                  placeholder="owner@example.com"
                  maxLength={255}
                  {...register("email")}
                />
                <TextInput
                  id="se-website"
                  label="Website"
                  placeholder="https://example.com"
                  maxLength={255}
                  {...register("website")}
                />
                <div className="border-t pt-4 space-y-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Public Contact
                  </p>
                  <TextInput
                    id="se-public-phone"
                    label="Public phone"
                    placeholder="+1 416 555 0100"
                    maxLength={50}
                    {...register("public_phone")}
                  />
                  <TextInput
                    id="se-public-email"
                    label="Public email"
                    type="email"
                    placeholder="hello@example.com"
                    maxLength={255}
                    {...register("public_email")}
                  />
                </div>
              </TabsContent>
            </Tabs>

            {errors.root?.message && (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={tryClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Spinner className="size-4 border-primary-foreground" />
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
